<template>
  <UiDialog
    :open="open"
    title="Word Lists Management"
    size="lg"
    @close="emit('close')"
  >
    <div class="space-y-4">
      <!-- Tabs for Filtering Word Lists -->
      <UiTabs
        v-model="activeTab"
        label="Word lists sections"
        :options="tabs"
        @update:model-value="onTabChange"
      />

      <!-- Part of Speech Pill Selector -->
      <div
        v-if="activeTab === 'part_of_speech'"
        class="flex flex-wrap items-center gap-1.5 p-2 rounded-xl border border-[var(--ui-border)] bg-[var(--ui-surface-subtle)]"
      >
        <span class="text-xs font-bold uppercase text-[var(--ui-text-muted)] mr-1">Category:</span>
        <UiButton
          v-for="pos in partsOfSpeech"
          :key="pos.id"
          size="sm"
          :variant="selectedPosId === pos.id ? 'primary' : 'ghost'"
          shape="pill"
          class="text-xs px-3"
          @click="selectPos(pos.id)"
        >
          {{ pos.name }}
        </UiButton>
      </div>

      <!-- Postponed Break Shifting Controls (100% Offline JS) -->
      <div
        v-if="activeTab === 'postponed' && items.length > 0"
        class="flex flex-wrap items-center justify-between gap-2 p-2.5 rounded-xl border border-[var(--ui-border)] bg-[var(--ui-surface-muted)]"
      >
        <span class="text-xs font-medium text-[var(--ui-text-subtle)]">
          Shift all postponed review dates:
        </span>
        <div class="flex flex-wrap items-center gap-1.5">
          <UiButton size="sm" variant="secondary" shape="pill" class="text-xs" @click="shiftPostponed(-1)">
            -1 Day
          </UiButton>
          <UiButton size="sm" variant="secondary" shape="pill" class="text-xs" @click="shiftPostponed(1)">
            +1 Day
          </UiButton>
          <UiButton size="sm" variant="secondary" shape="pill" class="text-xs" @click="shiftPostponed(3)">
            +3 Days
          </UiButton>
          <UiButton size="sm" variant="secondary" shape="pill" class="text-xs" @click="shiftPostponed(7)">
            +7 Days
          </UiButton>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="flex flex-col items-center justify-center py-12 gap-3">
        <UiSpinner size="lg" />
        <span class="text-xs text-[var(--ui-text-muted)]">Loading words...</span>
      </div>

      <!-- Empty State -->
      <UiEmptyState
        v-else-if="items.length === 0"
        title="No words found"
        description="There are no words matching this category in your dictionary yet."
        class="py-8"
      />

      <!-- Word List Items -->
      <div v-else class="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
        <div
          v-for="item in items"
          :key="item.word_id"
          class="flex items-center justify-between p-3 rounded-xl border border-[var(--ui-border)] bg-[var(--ui-surface-muted)] hover:border-[var(--ui-primary)]/40 transition gap-3"
        >
          <div class="flex flex-col min-w-0">
            <div class="flex items-center gap-2 flex-wrap">
              <span class="font-bold text-base text-[var(--ui-text)] truncate">{{ item.word }}</span>
              <UiBadge variant="warning" size="sm" class="font-mono">
                {{ '⭐'.repeat(Math.max(1, item.stars || 1)) }}
              </UiBadge>
              <span v-if="item.phonetics" class="text-xs font-mono text-[var(--ui-text-muted)]">
                [{{ item.phonetics }}]
              </span>
            </div>
            <div class="text-sm text-[var(--ui-text-muted)] mt-0.5 truncate">
              {{ item.translation || '—' }}
            </div>
            <div v-if="item.postponed_date" class="text-xs text-amber-600 dark:text-amber-400 mt-1 flex items-center gap-1">
              <i class="fa-solid fa-clock"></i>
              <span>Postponed until {{ formatDate(item.postponed_date) }}</span>
            </div>
          </div>

          <div class="flex items-center gap-1.5 shrink-0">
            <button
              type="button"
              class="audio-button w-8 h-8 rounded-full hover:scale-105 active:scale-95 transition flex items-center justify-center shadow-xs text-[var(--ui-primary)]"
              title="Pronounce word"
              @click="playPronounce(item.word)"
            >
              <i class="fa-solid fa-volume-high text-sm"></i>
            </button>
            <UiButton
              v-if="activeTab === 'removed'"
              size="sm"
              variant="secondary"
              @click="restoreWord(item.word_id)"
            >
              Restore
            </UiButton>
          </div>
        </div>
      </div>

      <!-- Pagination and Total Count -->
      <div class="flex items-center justify-between pt-3 border-t border-[var(--ui-border)]">
        <div class="text-xs text-[var(--ui-text-muted)]">
          Page {{ page + 1 }} of {{ totalPages || 1 }} ({{ total }} total words)
        </div>
        <div class="flex items-center gap-2">
          <UiButton
            size="sm"
            variant="secondary"
            :disabled="page <= 0 || loading"
            @click="prevPage"
          >
            <i class="fa-solid fa-chevron-left mr-1"></i> Previous
          </UiButton>
          <UiButton
            size="sm"
            variant="secondary"
            :disabled="page >= totalPages - 1 || loading"
            @click="nextPage"
          >
            Next <i class="fa-solid fa-chevron-right ml-1"></i>
          </UiButton>
          <UiButton
            size="sm"
            variant="ghost"
            @click="emit('close')"
          >
            Close
          </UiButton>
        </div>
      </div>
    </div>
  </UiDialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import {
  UiDialog,
  UiTabs,
  UiButton,
  UiBadge,
  UiSpinner,
  UiEmptyState,
} from '../ui';
import { api } from '../services/api';
import { localSync } from '../services/LocalSyncService';
import { learningEngine } from '../services/LearningWorkflowEngine';
import type { SyncWordRecord, WordListItem } from '../types';

const props = defineProps<{
  open: boolean;
  initialFilter?: string;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'word-restored'): void;
}>();

const tabs = [
  { value: 'active', label: 'Active Words' },
  { value: 'learned', label: 'Learned Words' },
  { value: 'postponed', label: 'Postponed Words' },
  { value: 'part_of_speech', label: 'By Part of Speech' },
  { value: 'removed', label: 'Removed Words' },
];

const activeTab = ref(props.initialFilter || 'active');
const items = ref<WordListItem[]>([]);
const total = ref(0);
const page = ref(0);
const limit = 15;
const loading = ref(false);

const partsOfSpeech = ref<Array<{ id: number; name: string }>>([]);
const selectedPosId = ref<number | null>(null);

const totalPages = computed(() => Math.ceil(total.value / limit) || 1);

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      if (props.initialFilter) {
        activeTab.value = props.initialFilter;
      }
      page.value = 0;
      void loadWords();
    }
  }
);

watch(
  () => props.initialFilter,
  (newFilter) => {
    if (newFilter) {
      activeTab.value = newFilter;
      page.value = 0;
      void loadWords();
    }
  }
);

const onTabChange = (tabId: string) => {
  activeTab.value = tabId;
  page.value = 0;
  void loadWords();
};

const selectPos = (posId: number) => {
  selectedPosId.value = posId;
  page.value = 0;
  void loadWords();
};

const prevPage = () => {
  if (page.value > 0) {
    page.value--;
    void loadWords();
  }
};

const nextPage = () => {
  if (page.value < totalPages.value - 1) {
    page.value++;
    void loadWords();
  }
};

const loadWords = async () => {
  loading.value = true;
  try {
    // 1. Always query local IndexedDB first so offline mode is instantaneous
    const localRes = await learningEngine.queryWordList({
      filter: activeTab.value,
      pos_id: activeTab.value === 'part_of_speech' ? selectedPosId.value : undefined,
      page: page.value,
      limit,
    });

    items.value = localRes.items || [];
    total.value = localRes.total || 0;
    if (localRes.parts_of_speech?.length) {
      partsOfSpeech.value = localRes.parts_of_speech;
      if (!selectedPosId.value) {
        selectedPosId.value = localRes.selected_pos_id || localRes.parts_of_speech[0].id;
      }
    }

    // 2. When online, also check remote list if local DB has few/no items or in mock environments
    if (localSync.state.connection !== 'offline' && typeof navigator !== 'undefined' && navigator.onLine) {
      try {
        const res = await api.getWordList({
          filter: activeTab.value,
          pos_id: activeTab.value === 'part_of_speech' ? selectedPosId.value : undefined,
          page: page.value,
          limit,
        });
        if (res.parts_of_speech?.length) {
          partsOfSpeech.value = res.parts_of_speech;
          if (!selectedPosId.value) {
            selectedPosId.value = res.selected_pos_id || res.parts_of_speech[0].id;
          }
        }
        if (res.items?.length) {
          // Cache remote items into IndexedDB and display
          const toUpsert: SyncWordRecord[] = res.items.map((w) => ({
            id: Number(w.word_id),
            word_id: Number(w.word_id),
            dictionary_id: w.dictionary_id || 1,
            language_code: 'ka',
            word: w.word,
            short_word: w.word,
            part_of_speech_id: w.part_of_speech_id ?? selectedPosId.value ?? 1,
            part_of_speech_name: w.part_of_speech_name ?? 'Noun',
            translations: w.translation ? { en: w.translation } : {},
            translation: w.translation || '',
            phonetics_map: w.phonetics ? { ipa: w.phonetics } : {},
            phonetics: w.phonetics || '',
            hint: '',
            stars: w.stars ?? 3,
            is_learned: Boolean(w.is_learned),
            postponed_date: w.postponed_date || null,
            sync_version: 1,
          }));
          await localSync.upsertWordsLocal(toUpsert);
          if (items.value.length === 0 || localSync.state.pendingWords === 0) {
            items.value = res.items;
            total.value = res.total || res.items.length;
          }
        }
      } catch {
        // Offline or network unreachable — local IndexedDB results are already rendered
      }
    }
  } catch (err) {
    console.warn('Failed to load words list:', err);
  } finally {
    loading.value = false;
  }
};

const playPronounce = (wordText: string) => {
  if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(wordText);
    utterance.lang = 'ka-GE';
    utterance.rate = 0.9;
    window.speechSynthesis.speak(utterance);
  }
};

const restoreWord = async (wordId: number) => {
  try {
    await learningEngine.reactToWord(wordId, 'restore');
    if (localSync.state.connection !== 'offline' && navigator.onLine) {
      void api.reactToWord({ word_id: wordId, action: 'star_up' }).catch(() => null);
    }
    emit('word-restored');
    await loadWords();
  } catch (err) {
    console.warn('Failed to restore word:', err);
  }
};

const shiftPostponed = async (days: number) => {
  await learningEngine.shiftPostponedDays(days);
  await loadWords();
};

const formatDate = (dateStr: string) => {
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return dateStr;
  }
};
</script>
